import { UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Radio, Upload, message } from 'antd';
import { Rule } from 'antd/lib/form';
import { UploadFile } from 'antd/lib/upload/interface';
import { Trans, useTranslation } from 'react-i18next';

import { MAX_SIZE_2MB } from '../consts';
import { fileTypeToMimeType, supportedFiles } from './EmailForm';
import Form from './Form';
import EditorCharCounter from './RichEditor/EditorCharCounter';
import RichEditor from './RichEditor/RichEditor';

interface EmailTemplateFormType {
  value: string;
  label: string;
  placeholder?: string;
}

interface EmailTemplateFormCustomVariable {
  value: string;
  label: string;
}

interface EmailTemplateFormCustomVariable {
  value: string;
  label: string;
}

export interface EmailTemplateFormValues {
  name: string;
  message: string;
  subject: string;
  type: string;
  fromName: string;
  attachments: UploadFile[];
  activeWelcomeTemplate?: boolean;
}

interface EmailTemplateFormProps {
  staticSubject?: boolean;
  customVariables?: EmailTemplateFormCustomVariable[];
  maxMessageLength?: number;
  templateTypes: EmailTemplateFormType[];
  image?: boolean;
  fromName?: boolean;
  attachments?: boolean;
  richEditorRules?: Rule[];
  disableActiveWelcomeTemplate?: boolean;
  disableTypeField?: boolean;
  typeTooltipMessage?: string;
}

const EmailTemplateForm = ({
  customVariables,
  maxMessageLength,
  templateTypes,
  staticSubject,
  image,
  attachments,
  richEditorRules,
  fromName,
  disableActiveWelcomeTemplate = false,
  disableTypeField = false,
  typeTooltipMessage = '',
}: EmailTemplateFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();

  const messageValue = Form.useWatch('message', form);
  const typeValue = Form.useWatch('type', form);

  return (
    <>
      <Form.Item label={t('Nome')} name="name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('Assunto')} name="subject" rules={[{ required: !staticSubject }]}>
        <Input
          disabled={staticSubject}
          placeholder={
            templateTypes.find((templateType) => templateType.value === typeValue)?.placeholder ||
            t('Retorno do processo seletivo')
          }
        />
      </Form.Item>
      <Form.Item
        label={t('Tipo')}
        name="type"
        rules={[{ required: true }]}
        tooltip={typeTooltipMessage}
      >
        <Radio.Group disabled={disableTypeField}>
          {templateTypes.map(({ label, value }) => (
            <Radio key={value} value={value}>
              {label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>
      {typeValue === 'WELCOME_MESSAGE' && (
        <Form.Item name="activeWelcomeTemplate" valuePropName="checked">
          <Checkbox disabled={disableActiveWelcomeTemplate}>
            <Trans>Usar esse modelo como padrão para mensagem de boas-vindas</Trans>
          </Checkbox>
        </Form.Item>
      )}
      {fromName && (
        <Form.Item label={t('Enviado por')} name="fromName" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      )}
      <Form.Item label={t('Corpo do email')} required>
        <Form.Item
          noStyle
          name="message"
          rules={[
            {
              validator(_, value) {
                if (!maxMessageLength || value.length <= maxMessageLength) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    t(
                      'A mensagem não pode ultrapassar {{maxMessageLength}} caracteres. O contador de caracteres também contabiliza tags HTML (por exemplo, <p></p>) conforme a formatação escolhida',
                      { maxMessageLength }
                    )
                  )
                );
              },
            },
            ...(richEditorRules || []),
          ]}
        >
          <RichEditor
            image={image}
            video={false}
            variables={customVariables}
            data-testid="rich-editor-input"
          />
        </Form.Item>
        {maxMessageLength && (
          <EditorCharCounter count={messageValue?.length || 0} limit={maxMessageLength} />
        )}
      </Form.Item>
      {attachments && (
        <Form.Item
          name="attachments"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            data-testid="upload-attachments"
            listType="text"
            accept={supportedFiles.join(',')}
            beforeUpload={(file: UploadFile) => {
              if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                if (!file.size) {
                  message.error(t('Erro com o arquivo.'));
                  return;
                }
                if (file.size >= MAX_SIZE_2MB) {
                  message.error(
                    t(
                      'O arquivo selecionado excede o limite de 2 MB. Por favor, envie um arquivo menor.'
                    )
                  );
                  return Upload.LIST_IGNORE;
                }
                return false;
              } else {
                message.error(
                  t(
                    'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                  )
                );
                return Upload.LIST_IGNORE;
              }
            }}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>
      )}
    </>
  );
};

export default EmailTemplateForm;
